import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { noteCategories, getArticlesByCategory } from '@/mocks/notes';

type NoteCategory = (typeof noteCategories)[number];

interface CategoryResultSectionProps {
  category: NoteCategory;
}

export default function CategoryResultSection({ category }: CategoryResultSectionProps) {
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const articles = useMemo(() => {
    const base = getArticlesByCategory(category.id);
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.tags.some((t) => t.toLowerCase().includes(q)) ||
        a.content.intro.toLowerCase().includes(q)
    );
  }, [query, category.id]);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, [category.id]);

  const clearSearch = () => setQuery('');

  const hasQuery = query.trim().length > 0;

  return (
    <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden bg-[#faf8f5]">
      {/* Subtle background texture */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[40vw] h-full opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20guzheng%20instrument%20close%20up%20detail%20silk%20strings%20wooden%20frame%20subtle%20texture%20background%2C%20soft%20warm%20natural%20lighting%2C%20minimal%20zen%20aesthetic%2C%20cream%20and%20wheat%20tones%2C%20artistic%20still%20life%20photography&width=900&height=800&seq=noteshero001&orientation=portrait"
            alt=""
            className="w-full h-full object-cover object-left"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#faf8f5] via-[#faf8f5]/70 to-transparent"></div>
        </div>
      </div>

      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20">
        {/* 返回全部文章 */}
        <Link
          to="/notes"
          className="inline-flex items-center gap-2 text-[0.9375rem] text-[#7a6248] font-medium mb-8 group cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform duration-300"></i>
          全部文章
        </Link>

        {/* 分類標題區 */}
        <div className="flex items-start gap-5 mb-6">
          <div
            className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full shrink-0"
            style={{ backgroundColor: `${category.color}20`, color: category.color }}
          >
            <i className={`${category.icon} text-2xl`}></i>
          </div>
          <div>
            <p className="text-[0.8125rem] tracking-[0.2em] text-[#b08060] font-medium uppercase mb-2">
              鳴箏筆記・分類
            </p>
            <h1
              className="font-serif-tc text-[#2c2420] leading-tight"
              style={{ fontSize: 'clamp(1.875rem, 4vw, 2.75rem)' }}
            >
              {category.label}
            </h1>
          </div>
        </div>

        <p className="text-[1.0625rem] leading-[1.8] text-[#7a6e62] mb-6 max-w-2xl">
          {category.description}
        </p>

        {/* 分類導覽 chips（active 狀態） */}
        <div className="flex flex-wrap gap-2 mb-8">
          {noteCategories.map((cat) => {
            const isActive = cat.id === category.id;
            return (
              <Link
                key={cat.id}
                to={`/notes?category=${cat.id}`}
                className={`px-4 py-1.5 rounded-full text-[0.875rem] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'text-white'
                    : 'border border-[#d4c4b0] text-[#7a6248] hover:bg-[#f5f0e8]'
                }`}
                style={isActive ? { backgroundColor: cat.color } : {}}
              >
                {cat.label}
              </Link>
            );
          })}
        </div>

        {/* 分類內搜尋 */}
        <div className="max-w-xl mb-10">
          <div
            className={`relative flex items-center gap-3 bg-white border-2 rounded-2xl px-5 py-3.5 transition-all duration-300 ${
              isFocused ? 'border-[#b08060]' : 'border-[#e0d4c0]'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-search-line text-[#b08060] text-xl"></i>
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={`在「${category.label}」中搜尋……`}
              className="flex-1 bg-transparent outline-none text-[1rem] text-[#3a2e22] placeholder-[#c0b4a0] text-sm"
            />
            {hasQuery && (
              <button
                onClick={clearSearch}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-[#f0ebe2] hover:bg-[#e0d8cc] transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-[#7a6248] text-base"></i>
              </button>
            )}
          </div>
          <p className="text-[0.8125rem] text-[#9a8878] mt-2">搜尋範圍：{category.label}</p>
        </div>

        {/* 文章卡片 */}
        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {articles.length > 0 ? (
            <>
              {hasQuery && (
                <p className="text-[0.9375rem] text-[#7a6e62] mb-5">
                  找到 <span className="text-[#5a3e22] font-semibold">{articles.length}</span> 篇相關文章
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/notes/${article.slug}`}
                    className="group block bg-white rounded-xl border border-[#ede5d8] hover:border-[#c8b89a] overflow-hidden transition-all duration-300 cursor-pointer h-full flex flex-col"
                  >
                    <div className="relative h-64 sm:h-72 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                      />
                      <span
                        className="absolute top-3 left-3 inline-block px-2.5 py-1 rounded-full text-white text-[0.75rem] font-medium whitespace-nowrap"
                        style={{ backgroundColor: category.color }}
                      >
                        {article.categoryLabel}
                      </span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3
                        className="font-serif-tc text-[#2c2420] leading-[1.4] mb-2.5 group-hover:text-[#7a6248] transition-colors"
                        style={{ fontSize: '1.0625rem' }}
                      >
                        {article.title}
                      </h3>
                      <p className="text-[0.875rem] leading-[1.65] text-[#7a6e62] line-clamp-2 flex-1">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#f0e8dc] text-[0.8125rem] text-[#a89880]">
                        <span>{article.publishedAt}</span>
                        <span>·</span>
                        <span>約 {article.readTime} 分鐘</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : hasQuery ? (
            /* 搜尋無結果 */
            <div className="text-center py-16">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#f0ebe2] mx-auto mb-4">
                <i className="ri-file-search-line text-[#b08060] text-2xl"></i>
              </div>
              <p className="text-[1rem] text-[#7a6e62] mb-1">
                在「{category.label}」中找不到「<strong className="text-[#3a2e22]">{query.trim()}</strong>」相關文章
              </p>
              <p className="text-[0.9375rem] text-[#a89880] mb-6">試試其他關鍵字，或回到全部文章瀏覽</p>
              <button
                onClick={clearSearch}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#c8b49a] text-[#7a6248] text-[0.9375rem] font-medium hover:bg-[#7a6248] hover:text-white hover:border-[#7a6248] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                清除搜尋
              </button>
            </div>
          ) : (
            /* 分類無文章 */
            <div className="text-center py-16">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#f0ebe2] mx-auto mb-4">
                <i className="ri-file-list-3-line text-[#b08060] text-2xl"></i>
              </div>
              <p className="text-[1rem] text-[#7a6e62] mb-1">「{category.label}」目前還沒有文章</p>
              <p className="text-[0.9375rem] text-[#a89880] mb-6">新的文章正在準備中，歡迎先看看其他分類</p>
              <Link
                to="/notes"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#c8b49a] text-[#7a6248] text-[0.9375rem] font-medium hover:bg-[#7a6248] hover:text-white hover:border-[#7a6248] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                返回全部文章
              </Link>
            </div>
          )}
        </div>

        {/* 底部返回 */}
        <div className="mt-14 flex justify-center">
          <Link
            to="/notes"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#c8b49a] text-[#7a6248] text-[0.9375rem] font-medium hover:bg-[#7a6248] hover:text-white hover:border-[#7a6248] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            查看全部文章
          </Link>
        </div>
      </div>
    </section>
  );
}